import React, { PropsWithChildren, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Modal } from '@swyftx/aviary/atoms/Modal';
import { ModalProps } from '@swyftx/aviary/atoms/Modal/Modal.styles';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { useSnackBars } from '@swyftx/aviary/complex/GlobalSnackBars/useSnackBars';
import { ReviewItem } from '@swyftx/aviary/molecules/ReviewItem/ReviewItem';
import { MarginOrderDirection, MarginOrderType, ProtectionOrderVariant } from '@swyftx/margin/build/types';
import { CreateMarginPosition } from '@swyftx/margin/build/types';

import { Modals } from '@global-components/Modals/Modals.enum';
import { useModal } from '@global-components/Modals/useModal.hooks';

import { formatCurrency } from '@shared/utils';

import { useMarginInstrumentDetails } from 'src/lib/margin/hooks/instruments';
import { useCreateMarginPosition } from 'src/lib/margin/hooks/positions';
import { useMarginTradePanelStoreUtils } from 'src/lib/margin/hooks/trade';

import { getErrorMessage, getSuccessMessage } from './MarginTradeReviewModal.utils';
import { MarginTradeReviewProtectionOrder } from './MarginTradeReviewProtectionOrder';
import {
  EstimatedMarginProfitLoss,
  TotalMarginRequired,
  MarginInstrument,
  TotalMarginPositionSize,
} from '../../General';
import { SwyftxMarginStalePriceBanner } from '../../General/SwyftxMarginStalePriceBanner';
import { MarginTradePanelMarginExpiry } from '../MarginTradePanel/components/MarginTradePanelReviewFooter/MarginTradePanelMarginExpiry';
import { MarginTradePanelMarginFee } from '../MarginTradePanel/components/MarginTradePanelReviewFooter/MarginTradePanelMarginFee';

type Props = {
  tradeSide: MarginOrderDirection;
  modalPosition?: ModalProps['position'];
};

const MarginTradeReviewModal: React.FC<PropsWithChildren<Props>> = ({
  tradeSide,
  modalPosition = 'dynamic',
  children,
}) => {
  const { t } = useTranslation('margin');
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const {
    selectedMarket,
    price,
    lots,
    tradeType,
    takeProfit,
    stopLoss,
    isValidTrade,
    buttonColor,
    onResetTradePanel,
    isStalePriceData,
    audPrice,
  } = useMarginTradePanelStoreUtils({
    tradeSide,
  });
  const { symbol, priceScale } = useMarginInstrumentDetails({ instrument: selectedMarket });
  const { createPosition } = useCreateMarginPosition();
  const { addSnackBar } = useSnackBars();
  const { openModal } = useModal();

  const onCreatePosition = useCallback(async () => {
    if (!selectedMarket) return;

    try {
      setLoading(true);

      const position: CreateMarginPosition = {
        marketUuid: selectedMarket.uuid,
        sell: tradeSide === MarginOrderDirection.Short,
        contracts: lots,
        limitPrice: tradeType === MarginOrderType.Limit ? price : undefined,
        stopPrice: tradeType === MarginOrderType.Stop ? price : undefined,
        takeProfit: takeProfit.checked ? takeProfit.price : undefined,
        stopLoss: stopLoss.checked ? stopLoss.price : undefined,
      };

      const response = await createPosition(position);

      addSnackBar({
        id: 'margin-order-success',
        variant: 'success',
        content: getSuccessMessage({ t, tradeType, tradeSide, symbol }),
        showIcon: true,
        label: 'View order',
        duration: 5_000,
        className: 'mb-48 md:mb-0',
        onClick: () => openModal(Modals.MarginPositionDetails, { positionId: response?.position.positionId }),
      });

      setOpen(false);
      onResetTradePanel();
    } catch (e) {
      addSnackBar({
        variant: 'error',
        duration: 5_000,
        content: getErrorMessage({ t, tradeType, tradeSide, symbol }),
        className: 'mb-48 md:mb-0',
        showIcon: true,
      });
    } finally {
      setLoading(false);
    }
  }, [
    addSnackBar,
    createPosition,
    lots,
    onResetTradePanel,
    openModal,
    price,
    selectedMarket,
    stopLoss.checked,
    stopLoss.price,
    symbol,
    t,
    takeProfit.checked,
    takeProfit.price,
    tradeSide,
    tradeType,
  ]);

  if (!selectedMarket) return null;

  return (
    <Modal
      triggerElement={children}
      title={t('tradeReview.reviewOrder')}
      className='lg:min-w-[400px] lg:max-w-[400px]'
      overlayClassName='z-modal-popover-backdrop'
      onOpenChange={(o) => setOpen(!!o)}
      position={modalPosition}
      open={open}
    >
      <FlexLayout
        direction='column'
        alignItems='center'
        justifyContent='space-between'
        className='h-full w-full px-24 pb-24'
        spacing={16}
      >
        <SwyftxMarginStalePriceBanner />
        <FlexLayout direction='column' spacing={16} className='h-full w-full'>
          <FlexLayout direction='column' spacing={12}>
            <ReviewItem title={t('tradeReview.labels.instrument')}>
              <MarginInstrument size='medium' weight='bold' market={selectedMarket} />
            </ReviewItem>
            <ReviewItem title={t('tradeReview.labels.orderType')}>
              <Body weight='bold'>{t(`tradeType.${tradeType}`)}</Body>
            </ReviewItem>
            <ReviewItem title={t('tradeReview.labels.side')}>
              <Body weight='bold'>{t(`tradeSide.${tradeSide}`)}</Body>
            </ReviewItem>
            <ReviewItem title={t('sizeLots')} tooltip={t('tradePanel.tooltips.sizeLots')} underline='dashed'>
              <Numeric color='primary' weight='bold'>
                {Number(lots).toFixed(priceScale)}
              </Numeric>
            </ReviewItem>
            <ReviewItem title={t('tradeReview.labels.price')}>
              <Numeric color='primary' weight='bold'>
                ≈ {formatCurrency(price, undefined, { priceScale: selectedMarket.precision })}
              </Numeric>
            </ReviewItem>
          </FlexLayout>

          {stopLoss.checked && selectedMarket && (
            <MarginTradeReviewProtectionOrder
              title={t('protectionOrders.stopLoss')}
              value={stopLoss.price}
              protectionOrderVariant={ProtectionOrderVariant.StopLoss}
            >
              <EstimatedMarginProfitLoss
                lots={lots}
                price={audPrice.toString()}
                currentPrice={price}
                protectionPrice={Number(stopLoss.price)}
                protectionVariant={ProtectionOrderVariant.StopLoss}
                instrument={selectedMarket}
                side={tradeSide}
                tradeType={tradeType}
                weight='bold'
              />
            </MarginTradeReviewProtectionOrder>
          )}
          {takeProfit.checked && selectedMarket && (
            <MarginTradeReviewProtectionOrder
              title={t('protectionOrders.takeProfit')}
              value={takeProfit.price}
              protectionOrderVariant={ProtectionOrderVariant.TakeProfit}
            >
              <EstimatedMarginProfitLoss
                lots={lots}
                price={audPrice.toString()}
                currentPrice={price}
                protectionPrice={Number(takeProfit.price)}
                protectionVariant={ProtectionOrderVariant.TakeProfit}
                instrument={selectedMarket}
                side={tradeSide}
                tradeType={tradeType}
                weight='bold'
              />
            </MarginTradeReviewProtectionOrder>
          )}
          <Separator />
          <Body size='xsmall' weight='emphasis' color='primary'>
            {t('tradeReview.labels.orderSummary')}
          </Body>
          <MarginTradePanelMarginExpiry />
          <ReviewItem
            title={t('position.estimatedCommission.title')}
            underline='dashed'
            tooltip={t('position.estimatedCommission.tooltip')}
            name={t('position.estimatedCommission.name')}
          >
            <MarginTradePanelMarginFee tradeSide={tradeSide} valueOnly />
          </ReviewItem>
          <TotalMarginPositionSize
            title={t('position.totalPositionSize.title')}
            lots={lots}
            price={audPrice.toString()}
            contractSize={selectedMarket?.contractSize}
          />
          <TotalMarginRequired
            title={t('position.totalMarginRequired.title')}
            lots={lots}
            price={audPrice.toString()}
            contractSize={selectedMarket?.contractSize}
            maxLeverage={selectedMarket?.maxLeverage}
          />
        </FlexLayout>
        <Button
          className='w-full'
          size='lg'
          color={buttonColor}
          disabled={!isValidTrade || loading || isStalePriceData()}
          tooltip={isStalePriceData() ? t('stalePrice.tooltips.placingOrders') : undefined}
          onClick={onCreatePosition}
          loading={loading}
        >
          {t(`tradeSide.${tradeSide}`)} {symbol}
        </Button>
      </FlexLayout>
    </Modal>
  );
};

export { MarginTradeReviewModal };
